/**
 * Écart d'offre (conception/ecart-offre.md, R4-R11) : l'accueil signale que
 * la carte présentée indique une autre offre que la plateforme ; la chargée
 * de clientèle tranche. Derrière `FONCTION_ECART_OFFRE`, éteint par défaut.
 */
import { z } from 'zod';
import { TYPES_BENEFICIAIRE } from '../rules/beneficiaire.js';
import { schemaPage, schemaPagination } from './administration.js';
export const STATUTS_SIGNALEMENT_OFFRE = ['OUVERT', 'TRANCHE', 'RETIRE'];
/** R9 — (a) la carte a raison pour cette personne, (b) pour tout le contrat, (c) la plateforme a raison. */
export const ISSUES_SIGNALEMENT_OFFRE = ['CARTE_PERSONNE', 'CARTE_CONTRAT', 'PLATEFORME'];
/**
 * Décision 178 : passé ce délai, l'écart passe en rouge dans la liste de la
 * chargée et la cloche la relance, une fois. Le patient attend au comptoir.
 */
export const DELAI_RELANCE_ECART_OFFRE_MINUTES = 10;
const note = z.string().trim().max(500);
/** R4 — l'offre lue sur la carte ; `null` = « Autre », dite alors par la note. */
export const schemaOuvertureSignalementOffre = z
    .object({ offreLueId: z.uuid().nullable(), note: note.optional() })
    .strict()
    .refine((s) => s.offreLueId !== null || (s.note ?? '').length > 0, {
    message: 'Une offre hors catalogue se décrit dans la note.',
    path: ['note'],
});
/**
 * R9 — la décision. `offreId` : l'offre à poser quand la carte a raison et
 * qu'elle disait « Autre » ; sinon l'offre lue s'applique (une autre est refusée).
 */
export const schemaDecisionSignalementOffre = z
    .object({
    issue: z.enum(ISSUES_SIGNALEMENT_OFFRE),
    note: note.optional(),
    offreId: z.uuid().optional(),
    /** Issue « tout le contrat » : le contrat que la chargée a vu ; refusé s'il a changé. */
    contratId: z.uuid().optional(),
    /**
     * Issue « tout le contrat » : l'offre du contrat que la chargée a vue ;
     * refusé si un autre poste l'a changée entre-temps (S317). Obligatoire avec
     * cette issue : sans elle, une mise à jour concurrente serait perdue.
     */
    offreDuContratId: z.uuid().optional(),
    /**
     * Issue « seulement ce patient » (décision 384) : le contrat en cours qu'il
     * rejoint, quand l'entreprise en a plusieurs sur l'offre de la carte, ou
     * après un refus CP-08 de « tout le contrat ». Sans lui, la plateforme
     * rejoint le seul contrat de cette offre, ou le crée.
     */
    versContratId: z.uuid().optional(),
})
    .strict()
    .refine((d) => d.issue === 'CARTE_PERSONNE' || d.versContratId === undefined, {
    message: 'Seul « seulement ce patient » fait rejoindre un autre contrat.',
    path: ['versContratId'],
})
    .refine((d) => d.issue !== 'PLATEFORME' || (d.note ?? '').length > 0, {
    message: 'Dites pourquoi la plateforme a raison : la note est obligatoire.',
    path: ['note'],
})
    .refine((d) => d.issue !== 'CARTE_CONTRAT' || d.contratId !== undefined, {
    message: 'Dites quel contrat change d’offre.',
    path: ['contratId'],
})
    .refine((d) => d.issue !== 'CARTE_CONTRAT' || d.offreDuContratId !== undefined, {
    message: 'Dites quelle offre du contrat vous avez lue.',
    path: ['offreDuContratId'],
});
/** R11 — retirer un signalement fait par erreur. */
export const schemaRetraitSignalementOffre = z
    .object({ motif: z.string().trim().min(3).max(500) })
    .strict();
export const schemaFiltresSignalementsOffre = schemaPagination.extend({
    statut: z.enum(STATUTS_SIGNALEMENT_OFFRE).default('OUVERT'),
});
const schemaOffreSignalee = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    rang: z.number().int().nullable(),
});
const schemaQui = z.object({ nom: z.string(), prenom: z.string().nullable() }).nullable();
/** Un signalement, tel que la chargée le tranche. */
export const schemaSignalementOffre = z.object({
    id: z.uuid(),
    statut: z.enum(STATUTS_SIGNALEMENT_OFFRE),
    issue: z.enum(ISSUES_SIGNALEMENT_OFFRE).nullable(),
    personne: z.object({
        id: z.uuid(),
        nom: z.string(),
        prenom: z.string().nullable(),
        matricule: z.string().nullable(),
        typeBeneficiaire: z.enum(TYPES_BENEFICIAIRE),
        /** Le titulaire qui porte l'offre ; lui-même pour un salarié. */
        titulaireId: z.uuid(),
    }),
    /**
     * Décision 395 — signalé sur un ayant droit, l'écart est porté par son
     * salarié : c'est lui que la chargée voit et tranche. `null` quand le
     * salarié est lui-même la personne signalée.
     */
    salarie: z
        .object({
        id: z.uuid(),
        nom: z.string(),
        prenom: z.string().nullable(),
        matricule: z.string().nullable(),
    })
        .nullable(),
    /**
     * Décision 395 — les membres de la famille sur qui un écart est ouvert
     * (« signalé sur : son enfant ») : une seule ligne par famille chez la chargée.
     */
    signaleSur: z.array(z.object({
        id: z.uuid(),
        nom: z.string(),
        prenom: z.string().nullable(),
        typeBeneficiaire: z.enum(TYPES_BENEFICIAIRE),
    })),
    entreprise: z.object({ id: z.uuid(), nom: z.string() }).nullable(),
    contrat: z
        .object({
        id: z.uuid(),
        populationLibelle: z.string().nullable(),
        /** L'offre du contrat : « tout le contrat SMIE de … » (G19, point 11). */
        offreNom: z.string(),
        offreId: z.uuid(),
        /** Les autres écarts ouverts de ce contrat que « tout ce contrat » règle aussi (S317). */
        autresEcartsOuverts: z.number().int(),
        /** « N salariés passeront en X » avant l'issue « tout le contrat ». */
        salariesActifs: z.number().int(),
    })
        .nullable(),
    site: z.object({ id: z.uuid(), nom: z.string() }),
    /** Figée à l'ouverture. */
    offrePlateforme: schemaOffreSignalee,
    offreLue: schemaOffreSignalee.nullable(),
    /** L'offre effective de la personne maintenant : un contrat corrigé entre-temps se voit. */
    offreActuelle: schemaOffreSignalee.nullable(),
    /** L'offre lue est déjà celle de la personne (contrat corrigé depuis) : à clore d'un clic. */
    dejaCorrige: z.boolean(),
    note: z.string().nullable(),
    noteDecision: z.string().nullable(),
    motifRetrait: z.string().nullable(),
    ouvertLe: z.iso.datetime(),
    ouvertPar: schemaQui,
    closLe: z.iso.datetime().nullable(),
    closPar: schemaQui,
    /** Réponse d'une décision seulement : les autres écarts réellement réglés avec elle (S317). */
    ecartsRegles: z.number().int().optional(),
});
export const schemaPageSignalementsOffre = schemaPage(schemaSignalementOffre);
/** L'interrupteur, tel que l'écran le lit pour montrer ou taire le signalement. */
export const schemaFonctionEcartOffre = z.object({ actif: z.boolean() });
//# sourceMappingURL=signalements-offre.js.map